import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import Can from 'Components/Authorize';
import { Types } from 'Components/Chat/ChatInfo/constants';
import ChatInfoContext from 'Components/Chat/ChatInfo/context';
import CloseButton from 'Components/modals/CloseButton';
import Tagging from 'Components/ShareModal/Tagging';
import EditTitle from 'Components/ShareModal/TitleEdit';
import VideoPlayer from 'Components/VideoPlayer';
import ViewersCount from 'Components/ViewersCount/standalone';
import { Box, Layer } from 'grommet';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-preact';
import { lazy, Suspense } from 'preact/compat';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { API_BASE_URL } from 'Shared/fetch';
import { trackMP } from 'Shared/mp';
import useFetch from 'use-http';

import { getMediaQueryOptions, updateMediaInQueryClient } from '@/queries/media';
import { useSelector } from '@/redux/helper';
import { addTagToMyAlbumContent, removeTagFromMyAlbumContent } from '@/redux/slices/myAlbum/actions';
import { addTagToHabitatAlbumContent, removeTagFromHabitatAlbumContent } from '@/routes/habitat/components/Album/actions';

import { useIsMobileSize } from '../../hooks';
import { getUrlParam, replaceUrlParam } from '../../routes/habitat/helpers';
import { legacyAlternateChatConfiguration } from '../Chat/ChatConfiguration/context';
import HabitatLink from '../HabitatLink';
import Image from '../Image';
import ShareButtons from './ShareButtons';
import style from './style.scss';

const Chat = lazy(() => import('Components/Chat'));
const PubNubWrapper = lazy(() => import('Components/PubNubWrapper'));

/**
 * ShareModal component.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.mediaId] - The ID of the media.
 * @param {boolean} [props.open] - Flag indicating whether the modal is open or not.
 * @param {boolean} [props.isMyAlbum] - Indicating whether the modal is called from MyAlbum component or not.
 * @param {object | null} [props.data] - The data object containing media information.
 * @param {string} [props.nextId] - The ID of the next media.
 * @param {string} [props.prevId] - The ID of the previous media.
 * @param {Function} props.onClose - The function to close the modal.
 * @param {Function} [props.setShareModalMediaId] - The function to set the media ID for the modal.
 * @param {Function} [props.onNext] - The function to be called when the next button is clicked.
 * @param {Function} [props.onPrev] - The function to be called when the prev button is clicked.
 * @param {string} [props.slug] - The slug of the media.
 * @param {Array} [props.hashtags] - The array of hashtags associated with the media.
 * @param {boolean} [props.isDownloadAllowed] - Flag indicating whether downloading is allowed or not.
 * @param {boolean} [props.shouldLoadPubnub] - Flag indicating whether PubNub should be loaded or not.
 * @param {boolean} [props.isDrawer] - Flag indicating whether the content should be rendered in a drawer or not.
 * @param {boolean} [props.skipDefaultCloseHandler] - Flag indicating whether the default close handler should be skipped or not.
 * @param {boolean} [props.mediaOnly] - Flag indicating whether to show only the media or not.
 * @returns {JSX.Element|null} The ShareModal component.
 */
const ShareModal = ({
  mediaId,
  open,
  data: inputData,
  nextId,
  prevId,
  onClose,
  setShareModalMediaId,
  onNext,
  onPrev,
  slug: inputSlug,
  hashtags: inputHashtags,
  isDownloadAllowed = true,
  shouldLoadPubnub = false,
  isDrawer = false, // to render the content in a drawer instead of a popup
  isMyAlbum = false,
  skipDefaultCloseHandler = false,
  mediaOnly = false,
}) => {
  const muted = useSelector((state) => state.settings.videoPlayerConfig.muted);
  const queryClient = useQueryClient();
  const isMobileSize = useIsMobileSize() || isDrawer;
  const { put: mediaViewedPut } = useFetch(API_BASE_URL, { credentials: 'include', cachePolicy: 'no-cache ' });
  const isGuestUser = useSelector((state) => !state.user.logged);
  const userId = useSelector((state) => state.user.userId);
  const currentHabitatId = useSelector((state) => state.habitat.habitatInfo?._id);

  const mediaQueryOptions = getMediaQueryOptions(mediaId);
  const { data: fetchedMedia, refetch } = useQuery(mediaQueryOptions);
  const onUpdateTitle = (newTitle) => {
    updateMediaInQueryClient(queryClient, mediaId, { title: newTitle });
  };

  const data = fetchedMedia ?? inputData;
  const {
    url,
    videoURL,
    rawURL,
    rawVideoURL,
    title,
    htmlURL,
    views,
    userId: ownerId,
    thumbnailURL,
    tags,
    habitatId,
    slugPrefix,
  } = data ?? {};

  const habitat =
    useSelector((state) => state.allHabitats.find(({ _id }) => _id === habitatId || _id === habitatId?._id)) ??
    data?.habitat;
  const resolvedHabitatSlug = habitat?.slug ? `${habitat.zoo.slug}/${habitat.slug}` : undefined;
  const slug = inputSlug ?? resolvedHabitatSlug;
  const type = inputData?.type ?? fetchedMedia?.type;
  const hashtags = inputHashtags ?? habitat?.shareSettings?.hashtag;
  const videoType = data?.videoType ?? data?.type;
  const mediaType = data?.mediaType ?? data?.type;

  const chatInfo = useMemo(
    () => ({
      chatType: mediaType === 'photo' ? Types.PHOTO : Types.CLIP,
      toUserId: ownerId,
      previewImage: thumbnailURL,
      mediaId,
    }),
    [mediaId, ownerId, thumbnailURL, mediaType],
  );

  useEffect(() => {
    if (mediaType === 'photo' && mediaId) {
      mediaViewedPut(`/photos/${mediaId}/viewed`).catch((err) => console.error(err));
    }
  }, [mediaId, mediaType, mediaViewedPut]);

  useEffect(() => {
    if (mediaId) {
      trackMP('watch-video', {
        type,
        videoURL,
        title,
        htmlURL,
        mediaId,
        habitatId,
      });
    }
  }, [mediaId, habitatId, type, videoURL, title, htmlURL]);

  // `closing` lets us unmount the video one render before the Layer tears down,
  // so Grommet's exit animation doesn't clone a still-playing <video> (audio leak)
  const [closing, setClosing] = useState(false);

  const onCloseRef = useRef({ onClose, skip: false });
  useEffect(() => {
    onCloseRef.current.onClose = onClose;
  }, [onClose]);

  // reset when the modal is (re)opened so a reused instance renders the video again
  useEffect(() => {
    if (open) {
      setClosing(false);
      onCloseRef.current.skip = false;
    }
  }, [open]);

  // once the video has unmounted (closing === true), run the actual close logic
  useEffect(() => {
    if (!closing) {
      return;
    }
    const id = getUrlParam('id');
    onCloseRef.current.skip = true;
    onClose();
    if (id && !skipDefaultCloseHandler) {
      if (window.history.length > 2) {
        // if history is more than 2 we assume user is navigating we go to previous page
        window.history.back();
      } else {
        // if history is less than 2 it means that the user opened this page from a URL
        // and we go back to the main page route
        window.location.href = `${window.location.origin}${window.location.pathname}`;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closing]);

  // if the modal is closed without clicking anywhere, e.g. called `history.back()`,
  // call onClose to notify the parent component
  useEffect(
    () => () => {
      if (!onCloseRef.current.skip) {
        onCloseRef.current.onClose?.(false);
      }
    },
    [],
  );

  if (!open || !data) {
    return null;
  }

  const onCloseHandler = () => {
    // defer the real close to the `closing` effect so the video unmounts first
    setClosing(true);
  };

  const navButtons = (
    <>
      {(nextId || onNext) && (
        <button
          type="button"
          className={clsx(style.next, isMobileSize && style.mobile)}
          onClick={() => {
            onNext?.();
            if (nextId) {
              setShareModalMediaId?.(nextId);
              route(replaceUrlParam('id', nextId), true);
            }
          }}
        >
          <ChevronRightIcon className="size-[1em]" />
        </button>
      )}
      {(prevId || onPrev) && (
        <button
          type="button"
          className={clsx(style.prev, isMobileSize && style.mobile)}
          onClick={() => {
            onPrev?.();
            if (prevId) {
              setShareModalMediaId?.(prevId);
              route(replaceUrlParam('id', prevId), true);
            }
          }}
        >
          <ChevronLeftIcon className="size-[1em]" />
        </button>
      )}
    </>
  );

  const content = (
    <>
      <div className={clsx(style.absoluteClose, isMobileSize && !mediaOnly && style.mobile)}>
        <CloseButton onClick={onCloseHandler} className={style.close} />
      </div>
      {!isMobileSize && !mediaOnly && navButtons}

      <Box
        className={clsx(
          style.shareModalContainer,
          isMobileSize && !mediaOnly && style.mobile,
          isDrawer && style.drawer,
          mediaOnly && style.mediaOnly,
        )}
      >
        {isMobileSize && !mediaOnly && <EditTitle data={data} mediaId={mediaId} onUpdate={onUpdateTitle} />}
        <Box direction="row">
          <Box className={style.shareMedia}>
            <Can perform="album:viewer-count" yes={() => <ViewersCount className={style.viewerCount} viewers={views} />} />
            {url && <Image className={clsx(isMobileSize && !mediaOnly && style.mobile)} src={rawURL} alt="" />}
            {mediaType !== 'photo' && (rawVideoURL || videoURL) && (
              <VideoPlayer
                videoURL={rawVideoURL ?? videoURL}
                className={clsx(style.videoPlayer, isMobileSize && !mediaOnly && style.mobile)}
                autoPlay={!closing}
                muted={muted || closing}
                isGuest={isGuestUser}
                videoId={mediaId}
                analyticsData={{
                  habitatId,
                  habitatName: habitat?.title,
                  slug,
                  title: data?.title,
                  player: 'ugc-modal',
                  videoType,
                }}
              />
            )}
            {isMobileSize && navButtons}
          </Box>
          {!mediaOnly && (
            <Box width={{ min: '285px', max: '320px' }} background="white" className={style.rightSection}>
              {!isMobileSize && (
                <EditTitle data={data} mediaId={mediaId} onUpdate={onUpdateTitle} editInPopup={mediaType !== 'photo'} />
              )}
              {!isGuestUser && (videoType === 'clip' || mediaType === 'photo') && (
                <div className={style.taggingWrapper}>
                  <Tagging
                    // add key to reset the internal state of the tagging component when the mediaId changes
                    key={mediaId}
                    mediaId={mediaId}
                    allTags={tags}
                    userId={userId}
                    type={mediaType === 'photo' ? 'photos' : 'videos'}
                    addTagToContent={isMyAlbum ? addTagToMyAlbumContent : addTagToHabitatAlbumContent}
                    removeTagFromContent={isMyAlbum ? removeTagFromMyAlbumContent : removeTagFromHabitatAlbumContent}
                    onUpdate={refetch}
                  />
                </div>
              )}
              {videoType !== 'trailer' && (
                <ShareButtons
                  mediaId={mediaId}
                  data={data}
                  slug={slug}
                  slugPrefix={slugPrefix}
                  hashtags={hashtags}
                  isDownloadAllowed={isDownloadAllowed}
                  userId={userId}
                  onDeleteSuccess={onCloseHandler}
                />
              )}
              <Box className={style.commentSection}>
                <Box fill>
                  {typeof window !== 'undefined' && (isGuestUser || shouldLoadPubnub) && (
                    <Suspense>
                      <PubNubWrapper isGuest={isGuestUser}>
                        <Suspense>
                          <ChatInfoContext.Provider value={chatInfo}>
                            <Chat
                              configuration={legacyAlternateChatConfiguration}
                              channelId={mediaId}
                              mediaType={type}
                              isGuest={isGuestUser}
                            />
                          </ChatInfoContext.Provider>
                        </Suspense>
                      </PubNubWrapper>
                    </Suspense>
                  )}

                  {typeof window !== 'undefined' && mediaId && !shouldLoadPubnub && (
                    <Suspense>
                      <ChatInfoContext.Provider value={chatInfo}>
                        <Chat configuration={legacyAlternateChatConfiguration} channelId={mediaId} mediaType={type} />
                      </ChatInfoContext.Provider>
                    </Suspense>
                  )}
                </Box>
              </Box>
              {resolvedHabitatSlug && currentHabitatId !== (habitatId?._id ?? habitatId) && (
                <div className="flex w-full shrink-0 justify-center bg-[#f2f4f5] p-4">
                  <HabitatLink
                    habitatId={habitat._id}
                    slug={habitat.slug}
                    zooSlug={habitat.zoo.slug}
                    className={clsx(
                      'preflight preflight-button block w-full rounded-full border-2 border-primary-green bg-primary-green px-4 text-center text-sm/6 font-medium text-white no-underline hover:border-secondary-green hover:bg-secondary-green',
                      isMobileSize ? 'py-3' : 'py-2',
                    )}
                    onClick={() => {
                      onCloseRef.current.skip = true;
                      onClose();
                    }}
                  >
                    Visit Habitat
                  </HabitatLink>
                </div>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );

  if (isDrawer) {
    return (
      <Layer
        data-testid="Media Modal"
        full="horizontal"
        animation="slide"
        position="bottom"
        className="!h-[93%] !max-w-full overflow-hidden !rounded-[20px] !rounded-b-none"
        onEsc={onCloseHandler}
        onClickOutside={onCloseHandler}
      >
        {content}
      </Layer>
    );
  }

  return (
    <Layer
      data-testid="Media Modal"
      animation="fadeIn"
      position="center"
      onClickOutside={onCloseHandler}
      onEsc={onCloseHandler}
      margin={{ horizontal: isMobileSize && !mediaOnly ? '0px' : '70px' }}
      full={isMobileSize && !mediaOnly}
      className={clsx(isMobileSize && !mediaOnly && style.fullModal, '!rounded-[8px]')}
    >
      {content}
    </Layer>
  );
};

export default ShareModal;
